import Typography from '@mui/material/Typography';
import MonsmryTable from './MonsmryTable';
import MonsmryTableWx from './MonsmryTableWx';
import { getAcisData, windchill, heatindex, setupFilters } from '../js/dataAccessRoutines';
import { valSmry, valExt, roundFixValue } from '../js/utilityRoutines';

// Frequencies and extremes processing //

// Codes for text values
const traceValue = Number.EPSILON;

const sendResults = (results, setResult, stnName, userParams) => {
  var code;
  if (results) {
      if (userParams.variable !== "wx") {
        code = (
          <MonsmryTable 
              results={{...results, stnName}}
          />
        );
      } else if (Object.keys(results.wxCodeData).length > 0) {
        code = (
          <MonsmryTableWx
              results={{...results, stnName}}
          />
        );
      } else {
        code = (
          <Typography sx={{color: "red"}}>
            No occurrences found for your selection of {userParams.wxvariables.join(", ")}.
          </Typography>
        );
      }
      setResult(code)
  } else {
    setResult("")
  }
}

// cleanup resultObs and get total counts
const mlySmry = (resultObs, sumObs, sumMiss, sumObsinSum, userParams) => {
  const start_month = userParams.fromDate.month();
  const start_year = userParams.fromDate.year();
  const end_month = userParams.toDate.month();
  const end_year = userParams.toDate.year();
  let totobscnt = 0, totmisscnt = 0;
  Object.keys(sumObs).forEach(yr => {
    sumObs[yr].forEach((mval,m) => {
      if ((Number(yr) === start_year && m < start_month) || (Number(yr) === end_year && m > end_month)) {
        // outside requested period of record
        resultObs[yr][m] = ""
      } else if (mval === 0 || (['max','min'].includes(userParams.smry) && Math.abs(resultObs[yr][m]) >= 9999)) {
        resultObs[yr][m] = "-"
      } else {
        if (userParams.smry === 'mean') {
          resultObs[yr][m] = sumObsinSum[yr][m] > 0 ? resultObs[yr][m] / sumObsinSum[yr][m] : '-'
        }
        if (userParams.variable === 'pcpn' && ['max','min','mean','sum'].includes(userParams.smry) && typeof resultObs[yr][m] === 'number') {
          resultObs[yr][m] = resultObs[yr][m] === traceValue ? traceValue: roundFixValue(Number(resultObs[yr][m]), 2)
        } else if (userParams.smry === 'mean' && typeof resultObs[yr][m] === 'number') {
          resultObs[yr][m] = roundFixValue(Number(resultObs[yr][m]), 1)
        } else if (userParams.smry === "pcthrs") {
          resultObs[yr][m] = roundFixValue(((resultObs[yr][m] / mval) * 100), 0)
        }
        totobscnt += mval
      }
      if (mval > 0 || sumMiss[yr][m] > 0) {
        const pctMiss = (sumMiss[yr][m] / (mval+sumMiss[yr][m])) * 100
        if (pctMiss > userParams.allowMissing) {
          resultObs[yr][m] = "M"
        }
        totmisscnt += sumMiss[yr][m]
      }
    })
  })
  return [resultObs, totobscnt, totmisscnt]
}

// get data for table and overall summaries
const tableSmry = (resultObs, userParams, months_to_use) => {
  const tabledata = []
  const overallSum = {}, overallCnt = {}, overallMax = {}, overallMin = {};
  Object.keys(resultObs).map((yy, i) => {
    const rowdata = {}
    rowdata.index = yy
    rowdata.year = userParams.fromFilterMonth <= userParams.toFilterMonth ? yy : (yy+"-"+(Number(yy)+1))
    months_to_use.map((mv) => {
      if (i === 0) {
        overallSum[mv] = 0;
        overallCnt[mv] = 0;
        overallMax[mv] = -Number.MAX_VALUE;
        overallMin[mv] = Number.MAX_VALUE;
      }
      const celldata = mv <= 11 
        ? resultObs[yy][mv] 
        : (resultObs.hasOwnProperty(Number(yy)+1) ? resultObs[Number(yy)+1][mv-12] : "")
      rowdata[mv] = celldata === traceValue ? "T" : celldata  //.replace("-","&#8209;")
      // process for period of record summaries
      if (!['','-','M'].includes(celldata)) {
        const mvval = typeof celldata !== "number" ? parseFloat(celldata) : celldata;
        overallSum[mv] += mvval
        overallCnt[mv] += 1;
        overallMax[mv] = Math.max(overallMax[mv], mvval);
        overallMin[mv] = Math.min(overallMin[mv], mvval);
      }
    })
    tabledata.push(rowdata)
  })
  return [tabledata, overallSum, overallCnt, overallMax, overallMin]
}

// compute period of record summaries
const overallSmry = (overallSum, overallCnt, overallMax, overallMin, userParams, months_to_use) => {
  let overallData = {}
  var rowsmry = {}
  months_to_use.map((mv) => {
    if (overallMax[mv] === -Number.MAX_VALUE) {
      rowsmry[mv] = "M";
    } else if (userParams.variable === 'pcpn' && ['max','min','mean','sum'].includes(userParams.smry)) {
      rowsmry[mv] = overallMax[mv] === traceValue ? "T" : roundFixValue(overallMax[mv], 2);
    } else if (userParams.smry === 'mean' && typeof overallMax[mv] === 'number') {
      rowsmry[mv] = roundFixValue(overallMax[mv], 1)
    } else {
      rowsmry[mv] = overallMax[mv];
    }
  })
  overallData.max = {...rowsmry}
  months_to_use.map((mv) => {
    if (overallMin[mv] === Number.MAX_VALUE) {
      rowsmry[mv] = "M";
    } else if (userParams.variable === 'pcpn' && ['max','min','mean','sum'].includes(userParams.smry)) {
      rowsmry[mv] = overallMin[mv] === traceValue ? "T" : roundFixValue(overallMin[mv], 2);
    } else if (userParams.smry === 'mean' && typeof overallMin[mv] === 'number') {
      rowsmry[mv] = roundFixValue(overallMin[mv], 1)
    } else {
      rowsmry[mv] = overallMin[mv];
    }
  })
  overallData.min = {...rowsmry}
  overallData.sum = overallSum
  overallData.cnt = overallCnt
  return overallData
}

const summarize = (rawResultObs, resultWxCodes, sumObs, sumObsinSum, sumMiss, userParams) => {
  // months to be displayed in table; when crossing year boundary, number months 12, 13, etc.
  let months_to_use = []
  for (let m = 0; m <= 11; m += 1) {
    if (userParams.fromFilterMonth <= userParams.toFilterMonth && m >= userParams.fromFilterMonth - 1 && m <= userParams.toFilterMonth - 1) {
      months_to_use.push(m)
    } else if (userParams.fromFilterMonth > userParams.toFilterMonth && (m >= userParams.fromFilterMonth - 1 || m <= userParams.toFilterMonth - 1)) {
      months_to_use.push(m < userParams.toFilterMonth ? m + 12 : m)
    }
  }
  months_to_use.sort((a, b) => a - b)

  // cleanup resultObs and get total counts
  const [resultObs, totobscnt, totmisscnt] = mlySmry(rawResultObs, sumObs, sumMiss, sumObsinSum, userParams)
  // get data for table and overall summaries
  const [tabledata, overallSum, overallCnt, overallMax, overallMin] = tableSmry(resultObs, userParams, months_to_use)
  // compute period of record summaries
  const overallData = overallSmry(overallSum, overallCnt, overallMax, overallMin, userParams, months_to_use)

  const wxCodeData = {}
  if (userParams.variable === "wx") {
    const wxCodes = Object.keys(resultWxCodes)
    if (wxCodes.length > 0) {
      wxCodes.forEach(code => {
        const [wxresultObs] = mlySmry(resultWxCodes[code], sumObs, sumMiss, sumObsinSum, userParams)
        const [wxtabledata] = tableSmry(wxresultObs, userParams, months_to_use)
        wxCodeData[code] = wxtabledata
      })
    }
  }
  return {tabledata, overallData, wxCodeData, totobscnt, totmisscnt, months_to_use}
}

const processData = (res, userParams) => {
  var vals, cntresult, extresult;
  const { sd, ed, hours_to_use } = setupFilters(userParams)
  const syr = parseInt(res.data[0][0].split("-")[0])
  const eyr = parseInt(res.data.slice(-1)[0][0].split("-")[0])
  const noVal = 'na'  //code for when wind chill or heat index input outside range
  //initializations
  let sumObs = {}
  let sumObsinSum = {}
  let sumMiss = {}
  let resultObs = {}
  let resultWxCodes = {}
  for (let yr = syr; yr <= eyr; yr += 1) {
    sumObs[yr] = Array(12).fill(0)
    sumObsinSum[yr] = Array(12).fill(0)
    sumMiss[yr] = Array(12).fill(0)
    if (userParams.smry === 'max') {
      resultObs[yr] = Array(12).fill(-9999)
    } else if (userParams.smry === 'min') {
      resultObs[yr] = Array(12).fill(9999)
    } else {
      resultObs[yr] = Array(12).fill(0)

    }
  }
  //process
  res.data.forEach((results, i) => {
    let [yy, mm, dd] = results[0].split("-")
    yy = parseInt(yy)
    mm = parseInt(mm)
    let mmdd = String(mm).padStart(2, '0') + "-" + String(dd).padStart(2, '0');
    // month filter
    if ((sd <= ed && mmdd >= sd && mmdd <= ed) || (sd > ed && (mmdd >= sd || mmdd <= ed))) {
      let daycnt = 0
      let wxdaycnt = {}
      // derived variables for all hours in day
      if (userParams.variable === 'wchill') {
        vals = windchill(results[1], results[2], noVal)
      } else if (userParams.variable === 'htindx') {
        vals = heatindex(results[1], results[2], noVal)
      } else {
        vals = results[1]
      }
      // loop through all hours in the day
      vals.forEach((rval, hh) => {
        if (hours_to_use.includes(hh+1)) {  //first hour is 01, thus hh+1 here
          if (rval !== "M" ) {
            if (rval === "-0") { rval = "0"}
            if (userParams.variable === "pcpn" && rval === "T") { 
                rval = traceValue 
            }
            const val = rval !== noVal && userParams.variable !== 'wx' ? parseFloat(rval) : rval
            switch (userParams.smry) {
              case 'cnthrs':
              case 'pcthrs':
                cntresult = valSmry(val, userParams, traceValue);
                resultObs[yy][mm-1] += cntresult;
                break;
              case 'cntdys':
                cntresult = valSmry(val, userParams, traceValue);
                if (cntresult === 1 && daycnt === 0) {
                  daycnt = 1;
                  resultObs[yy][mm-1] += 1;
                }
                break;
              case 'max':
              case 'min':
                extresult = valExt(val, userParams, resultObs[yy][mm-1]);
                if (extresult !== resultObs[yy][mm-1]) {
                  resultObs[yy][mm-1] = extresult;
                }
                break;
              case 'sum':
              case 'mean':
                resultObs[yy][mm-1] += val;
                sumObsinSum[yy][mm-1] += 1;
                break;
              default:
                console.log('Unexpected summary type');
            }
            // for present weather find all full present weather codes containing requested abbreviations
            if (userParams.variable === 'wx' && cntresult === 1) {
              const wxcodes = val.split(" ");
              wxcodes.forEach((code) => {
                userParams.wxvariables.forEach((wxv) => {
                  if (code.includes(wxv)) {
                    if (!resultWxCodes[code]) {
                      resultWxCodes[code] = {}
                      for (let yr = syr; yr <= eyr; yr += 1) {
                        resultWxCodes[code][yr] = Array(12).fill(0)
                      }
                    }
                    if (userParams.smry !== 'cntdys') {
                      resultWxCodes[code][yy][mm-1] += 1;
                    } else {
                      if (!Object.keys(wxdaycnt).includes(code)) {
                        wxdaycnt[code] = 0
                      }
                      if (wxdaycnt[code] === 0) {
                        wxdaycnt[code] = 1;
                        resultWxCodes[code][yy][mm-1] += 1;
                      }
                    }
                  }                  
                })
              })
            }
            sumObs[yy][mm-1] += 1
          } else {
            sumMiss[yy][mm-1] += 1
          }
        }
      })
    }
  })

  const {tabledata, overallData, wxCodeData, totobscnt, totmisscnt, months_to_use} = summarize(resultObs, resultWxCodes, sumObs, sumObsinSum, sumMiss, userParams)
  return {tabledata, overallData, wxCodeData, totobscnt, totmisscnt, months_to_use, userParams}
}

export function processMonsmry(userParams, setResult, hourlyVx) {
  if (userParams.variable === 'wx' && !['cnthrs', 'cntdys'].includes(userParams.smry)) {
      setResult((
        <Typography sx={{color: "red"}}>
          This summary type is not available for present weather. Select "Number of hours" or "Number of days".
        </Typography>
      ))
    } else if (userParams.variable === 'wx' && userParams.wxvariables.length === 0) {
      setResult((
        <Typography sx={{color: "red"}}>
          Must check at least one weather type.
        </Typography>
      ))
  } else {
      const sid = userParams.sid;
      const network = sid.indexOf(" ") > 0 ? sid.split(" ")[1] : 'icao';
      // some variables need to be calculated from others; get what's needed
      var elems;
      if (userParams.variable === 'wchill') {
        elems = [ {vX: hourlyVx[network]['temp']}, {vX: hourlyVx[network]['wspd']} ]
      } else if (userParams.variable === 'htindx') {
        elems = [ {vX: hourlyVx[network]['temp']}, {vX: hourlyVx[network]['rhum']} ]
      } else {
        elems = [ {vX: hourlyVx[network][userParams.variable]} ]
      }
      // results as one record per day
      const acisParams = {
          sid: sid,
          sDate: userParams.fromDate.set('date', 1).format('YYYY-MM-DD'),   //start and end months need to be full months
          eDate: userParams.toDate.endOf('month').format('YYYY-MM-DD'),
          elems: elems
      }
      getAcisData(acisParams)
      .then(res => {
        const results = processData(res, userParams);
        const stnName = res.meta.name + ", " + res.meta.state;
        sendResults(results, setResult, stnName, userParams)
      })
      .catch(error =>  { 
        console.log("Data retrieval error: " + error); 
        setResult((
          <Typography sx={{color: "red"}}>
            Error processing request
          </Typography>
        ))
      })
  }
}
